import { MediaConstants } from '@accedo/xdk-core'
import { Lightning } from '@lightningjs/sdk'

import { Images } from '../../constants/paths'
import { PLAYBACK_SIGNALS } from '../../constants/signals'
import { KEY_CODE } from '../../constants/videoPlayer'
import { getText } from '../../utils/dictionary'
import { getAsset } from '../../utils/image'
import SpeechComp from '../speechComp/speechComp'
import { GenericPlayerButton, PlayerButton, PlayPauseButton } from './playerButton'

const {
  STATE: { PLAYING },
} = MediaConstants

export const enum PlayerControlButton {
  PlayPause = 'PlayPause',
  Scores = 'Scores',
  Brackets = 'Brackets',
  Feedback = 'Feedback',
}

export default class ControlButtons extends SpeechComp {
  private _buttonList = {}
  private _buttonNamesList = []
  private _buttonIndex = 0

  static _template() {
    return {
      w: 1920,
      h: 150,
      flex: {
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        direction: 'row',
      },
      PlayPause: {
        type: PlayPauseButton,
        ref: 'PlayPause',
        images: {
          play: Images.PLAYER_PLAY,
          pause: Images.PLAYER_PAUSE,
        },
        labels: {
          play: getText('playButton'),
          pause: getText('pauseButton'),
        },
        buttonIndex: 0,
      },
      Separator: {
        w: 84,
        h: 84,
        visible: false,
        flex: { direction: 'column', alignItems: 'center', justifyContent: 'center' },
        SeparatorContent: {
          flexItem: {
            marginLeft: 24,
            marginRight: 12,
            alignItems: 'center',
            justifyContent: 'center',
          },
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.PLAYER_SEPARATOR), 2, 36),
        },
      },
      Scores: {
        type: GenericPlayerButton,
        flexItem: {
          marginLeft: 12,
        },
        images: {
          main: Images.PLAYER_SCORES_OFF,
        },
        labels: {
          focused: getText('playerScoresButton'),
        },
        visible: false,
      },
      Brackets: {
        type: GenericPlayerButton,
        flexItem: {
          marginLeft: 12,
        },
        images: {
          main: Images.PLAYER_BRACKETS_OFF,
        },
        labels: {
          focused: getText('playerBracketsButton'),
        },
        visible: false,
      },
      Feedback: {
        type: GenericPlayerButton,
        flexItem: {
          marginLeft: 12,
        },
        images: {
          main: Images.PLAYER_FEEDBACK_OFF,
        },
        labels: {
          focused: getText('playerFeedbackButton'),
        },
        visible: false,
      },
    }
  }

  _handleLeftCommon() {
    this.updateButton(this._buttonNamesList[--this._buttonIndex])
    this.restartControlsTimer()
  }

  _handleRightCommon() {
    if (this._buttonIndex < this._buttonNamesList.length - 1) {
      this.updateButton(this._buttonNamesList[++this._buttonIndex])
    }
    this.restartControlsTimer()
  }

  static _states() {
    return [
      class PlayPause extends this {
        _handleLeft() {
          this.restartControlsTimer()
        }

        _handleRight() {
          this._handleRightCommon()
        }

        _handleEnter() {
          this.sendCurrentSignal()
          this.restartControlsTimer()
        }

        sendCurrentSignal() {
          this.playPause()
        }
      },
      class Scores extends this {
        _handleLeft() {
          this._handleLeftCommon()
        }

        _handleRight() {
          this._handleRightCommon()
        }

        _handleEnter() {
          this.sendCurrentSignal()
          this.restartControlsTimer()
        }

        sendCurrentSignal() {
          this.scoresModal()
        }
      },
      class Brackets extends this {
        _handleLeft() {
          this._handleLeftCommon()
        }

        _handleRight() {
          this._handleRightCommon()
        }

        _handleEnter() {
          this.sendCurrentSignal()
          this.restartControlsTimer()
        }

        sendCurrentSignal() {
          this.bracketsModal()
        }
      },
      class Feedback extends this {
        _handleLeft() {
          this._handleLeftCommon()
        }

        _handleRight() {
          this.restartControlsTimer()
        }

        _handleEnter() {
          this.sendCurrentSignal()
          this.restartControlsTimer()
        }

        sendCurrentSignal() {
          this.feedbackModal()
        }
      },
    ]
  }

  get buttons() {
    return [this.scores, this.brackets, this.feedback]
  }

  get playPauseButton() {
    return this.tag('PlayPause') as PlayPauseButton
  }

  get scores() {
    return this.tag('Scores') as GenericPlayerButton
  }

  get brackets() {
    return this.tag('Brackets') as GenericPlayerButton
  }

  get feedback() {
    return this.tag('Feedback') as GenericPlayerButton
  }

  get separator() {
    return this.tag('Separator')
  }

  set playerState(value) {
    this.playPauseButton.isPlaying = value === PLAYING
  }

  get playerState() {
    return this.playPauseButton.isPlaying
  }

  get focusedButton() {
    return this.tag(this.state) as PlayerButton
  }

  _init() {
    this._buttonList[PlayerControlButton.Scores] = this.scores
    this._buttonList[PlayerControlButton.Brackets] = this.brackets
    this._buttonList[PlayerControlButton.Feedback] = this.feedback

    this._buttonNamesList = [PlayerControlButton.PlayPause]
    this._setState(PlayerControlButton.PlayPause)
  }

  async _enable() {
    this.focusedButton.unfocusState()
  }

  _getFocused() {
    return this.tag(this.state)
  }

  _handleKey({ keyCode }) {
    this.restartControlsTimer()

    switch (keyCode) {
      case KEY_CODE.RW:
      case KEY_CODE.MEDIA_RW:
        this.jumpBackward()
        break
      case KEY_CODE.PLAY_PAUSE:
      case KEY_CODE.MEDIA_PLAY_PAUSE:
        this.playPause()
        break
      case KEY_CODE.MEDIA_PLAY:
        this.play()
        break
      case KEY_CODE.MEDIA_PAUSE:
        this.pause()
        break
      case KEY_CODE.FF:
      case KEY_CODE.MEDIA_FF:
        this.jumpForward()
        break
      case KEY_CODE.MEDIA_STOP:
        this.stop()
        break
      default:
        return false
    }
  }

  jumpBackward() {
    this.signal(PLAYBACK_SIGNALS.jumpBackward)
  }

  playPause() {
    if (this.playerState) {
      this.pause()
      SpeechComp.speakCustom('Paused')
    } else {
      this.play()
      SpeechComp.speakCustom('Playing')
    }
  }

  play() {
    this.signal(PLAYBACK_SIGNALS.play)
  }

  pause() {
    this.signal(PLAYBACK_SIGNALS.pause)
  }

  stop() {
    this.signal(PLAYBACK_SIGNALS.stop)
  }

  jumpForward() {
    this.signal(PLAYBACK_SIGNALS.jumpForward)
  }

  scoresModal() {
    this.signal(PLAYBACK_SIGNALS.scoresModal)
  }

  bracketsModal() {
    this.signal(PLAYBACK_SIGNALS.bracketsModal)
  }

  feedbackModal() {
    this.signal(PLAYBACK_SIGNALS.feedbackModal)
  }

  initFocus(keyCode) {
    if (!this.playerState) {
      switch (keyCode) {
        case KEY_CODE.LEFT:
        case KEY_CODE.RW:
        case KEY_CODE.MEDIA_RW:
          return
        case KEY_CODE.UP:
        case KEY_CODE.PLAY_PAUSE:
        case KEY_CODE.MEDIA_PLAY_PAUSE:
        case KEY_CODE.MEDIA_PLAY:
        case KEY_CODE.MEDIA_PAUSE:
          this.updateButton(PlayerControlButton.PlayPause)
          return
        case KEY_CODE.RIGHT:
        case KEY_CODE.FF:
        case KEY_CODE.MEDIA_FF:
          return
      }
    }
  }

  updateButton(buttonId: string) {
    this.focusedButton.unfocusState()
    this._setState(buttonId)
    this.focusedButton.focusState()
  }

  restartControlsTimer() {
    this.signal(PLAYBACK_SIGNALS.restartControlsTimer)
  }

  setButtonActive(btn: PlayerControlButton) {
    this._buttonList[btn].visible = true
    this._buttonNamesList.push(btn)
    this.separator.visible = true
  }

  $updateFocusByHover(button) {
    this._setState(button.ref)
    const buttonIndex = this._buttonNamesList.findIndex((btnName) => btnName === button.ref)
    this._buttonIndex = buttonIndex < 0 ? 0 : buttonIndex

    this.fireAncestors('$updateFocusByHover', this)
  }

  _handleClick() {
    ;(this as any)._handleEnter()
  }
}
